import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AppPromoLabel } from '@hero/schema';
import { ZapIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

export interface AppPromoLabelBadgeProps {
  label: AppPromoLabel;
  /** `sm` for card corner badges; `md` for modal headers. */
  size?: 'sm' | 'md';
  className?: string;
}

const STYLES: Record<AppPromoLabel, string> = {
  new: 'bg-emerald-500 text-white shadow-md shadow-emerald-500/40 ring-2 ring-emerald-400/25',
  soon: 'bg-gray-100 text-gray-600',
  beta: 'bg-violet-100 text-violet-700 ring-1 ring-violet-200/80',
  popular: 'bg-sky-100 text-sky-700 ring-1 ring-sky-200/80',
};

/** Only labels that change the card CTA away from the default primary button. */
const CONNECT_BUTTON_STYLES: Partial<Record<AppPromoLabel, string>> = {
  soon:
    'border-gray-200 bg-gray-100 text-gray-600 hover:border-gray-300 hover:bg-gray-200 hover:text-gray-700',
};

const DEFAULT_CONNECT_BUTTON =
  'border-primary-200 bg-primary-50 text-primary hover:border-primary hover:bg-primary hover:text-white';

/**
 * Connect / manage / request-access button classes for marketplace cards.
 */
export function appPromoConnectButtonClassName(
  label: AppPromoLabel | undefined,
  options?: { connected?: boolean; disabled?: boolean },
): string {
  const { connected = false, disabled = false } = options ?? {};
  const promo =
    !connected && label && CONNECT_BUTTON_STYLES[label]
      ? CONNECT_BUTTON_STYLES[label]
      : DEFAULT_CONNECT_BUTTON;

  return cn(
    'mt-1 rounded-lg border py-2 text-sm font-semibold transition-colors',
    promo,
    disabled && 'cursor-not-allowed opacity-60',
  );
}

/**
 * Marketplace card shell — `new` apps get a tinted border and soft glow until connected.
 */
export function appPromoCardClassName(
  label: AppPromoLabel | undefined,
  options?: { connected?: boolean },
): string {
  const { connected = false } = options ?? {};
  const highlightNew = label === 'new' && !connected;

  return cn(
    'group flex flex-col gap-3 rounded-xl border p-4 transition-all',
    highlightNew
      ? 'border-emerald-300 bg-gradient-to-b from-emerald-50 to-white shadow-md shadow-emerald-500/10 ring-1 ring-emerald-500/15 hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/15'
      : 'border-gray-200 bg-white hover:border-primary-200',
  );
}

/**
 * Promotional badge for marketplace cards and connect modals (`new`, `soon`, …).
 */
export function AppPromoLabelBadge({
  label,
  size = 'md',
  className,
}: AppPromoLabelBadgeProps) {
  const { t } = useTranslation('m-apps');

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full font-semibold',
        size === 'sm' ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[11px]',
        label === 'new' && size === 'md' && 'px-2.5 py-1 text-xs',
        STYLES[label],
        className,
      )}>
      {label === 'new' ? <ZapIcon className={size === 'sm' ? 'size-2.5' : 'size-3'} /> : null}
      {t(`label.${label}`)}
    </span>
  );
}
